<template>
  <div
    v-if="show && text"
    ref="tooltipContainer"
    class="tw-fixed tw-z-[60] tw-max-w-[254px] tw-whitespace-normal tw-rounded-md tw-bg-gray-900 tw-px-3 tw-py-3 tw-text-xs tw-text-white tw-shadow-lg dark:tw-bg-gray-700"
    :style="tooltipPosition"
  >
    {{ text }}
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'StudioTooltip',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    text: {
      type: String,
      default: ''
    },
    targetElement: {
      type: Object as () => Element | null,
      default: null
    }
  },
  data() {
    return {
      tooltipPosition: { top: '0px', left: '0px' }
    };
  },
  watch: {
    show(newValue: boolean) {
      if (newValue && this.targetElement) {
        this.$nextTick(() => {
          this.calculateTooltipPosition();
        });
      }
    },
    targetElement() {
      if (this.show && this.targetElement) {
        this.$nextTick(() => {
          this.calculateTooltipPosition();
        });
      }
    }
  },
  methods: {
    calculateTooltipPosition() {
      if (!this.targetElement) {
        return;
      }

      // First, position tooltip off-screen to measure its actual dimensions
      this.tooltipPosition = { top: '-9999px', left: '-9999px' };

      this.$nextTick(() => {
        const tooltipEl = this.$refs.tooltipContainer as HTMLElement;
        if (!tooltipEl) {
          return;
        }

        const rect = this.targetElement!.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const gap = 8;
        const minMargin = 10;

        // Get actual tooltip dimensions
        const tooltipRect = tooltipEl.getBoundingClientRect();
        const tooltipWidth = tooltipRect.width;
        const tooltipHeight = tooltipRect.height;

        let tooltipLeft = 0;
        let tooltipTop = 0;
        let position = 'right'; // Track which position we choose

        // Check available space in each direction
        const spaceRight = viewportWidth - rect.right;
        const spaceLeft = rect.left;
        const spaceTop = rect.top;
        const spaceBottom = viewportHeight - rect.bottom;

        // Priority order: right, left, bottom, top
        if (spaceRight >= tooltipWidth + gap + minMargin) {
          // Position to the right
          tooltipLeft = rect.right + gap;
          tooltipTop = rect.top + (rect.height - tooltipHeight) / 2;
          position = 'right';
        } else if (spaceLeft >= tooltipWidth + gap + minMargin) {
          // Position to the left
          tooltipLeft = rect.left - tooltipWidth - gap;
          tooltipTop = rect.top + (rect.height - tooltipHeight) / 2;
          position = 'left';
        } else if (spaceBottom >= tooltipHeight + gap + minMargin) {
          // Position below
          tooltipLeft = rect.left + (rect.width - tooltipWidth) / 2;
          tooltipTop = rect.bottom + gap;
          position = 'bottom';
        } else if (spaceTop >= tooltipHeight + gap + minMargin) {
          // Position above
          tooltipLeft = rect.left + (rect.width - tooltipWidth) / 2;
          tooltipTop = rect.top - tooltipHeight - gap;
          position = 'top';
        } else {
          // Fallback: position to the side with more space
          if (spaceRight >= spaceLeft) {
            tooltipLeft = rect.right + gap;
            tooltipTop = rect.top + (rect.height - tooltipHeight) / 2;
            position = 'right';
          } else {
            tooltipLeft = rect.left - tooltipWidth - gap;
            tooltipTop = rect.top + (rect.height - tooltipHeight) / 2;
            position = 'left';
          }
        }

        // Ensure tooltip stays within viewport bounds
        if (position === 'right' || position === 'left') {
          // Vertical centering adjustments for side positioning
          if (tooltipTop < minMargin) {
            tooltipTop = minMargin;
          } else if (tooltipTop + tooltipHeight > viewportHeight - minMargin) {
            tooltipTop = viewportHeight - tooltipHeight - minMargin;
          }
        } else {
          // Horizontal centering adjustments for top/bottom positioning
          if (tooltipLeft < minMargin) {
            tooltipLeft = minMargin;
          } else if (tooltipLeft + tooltipWidth > viewportWidth - minMargin) {
            tooltipLeft = viewportWidth - tooltipWidth - minMargin;
          }
        }

        // Final bounds check to ensure tooltip is always visible
        tooltipLeft = Math.max(minMargin, Math.min(tooltipLeft, viewportWidth - tooltipWidth - minMargin));
        tooltipTop = Math.max(minMargin, Math.min(tooltipTop, viewportHeight - tooltipHeight - minMargin));

        this.tooltipPosition = {
          top: `${tooltipTop}px`,
          left: `${tooltipLeft}px`
        };
      });
    }
  }
});
</script>
